import {
  SubheaderContainer,
  SubheaderInformationContainer,
} from "../../../../../components/Layout/Layout";

import {
  ButtonContainer,
  ButtonWithConfirmDialog,
  DestructiveButtonMediumV2,
  PrimaryButtonMediumV2,
  TertiaryButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import {
  H4,
  InnerTitleText,
  SubheaderContentText,
  XLTitle,
} from "../../../../../components/Typography/Typography";
import { TFunction, useTranslation } from "react-i18next";
import { useRoutePath } from "../../../../../util/Routing";
import styled, { useTheme } from "styled-components";
import { useCallback, useMemo } from "react";
import {
  CalendarIcon,
  TemplatesNavIcon,
  UserIcon,
} from "../../../../../components/Icons/Icons";
import {
  convertUserToOption,
  formatDateTime,
  useFormWrapper,
  useStoreState,
} from "../../../../../util/util";
import { StatusLeft } from "../../../../../components/Status/Status";
import {
  ContentWrapper,
  FullWidthHorizontalSeparator,
  PageWrapper,
  WhiteInnerCard,
} from "../../../../../layout/portalPageLayout";
import { WizardPath } from "../../../../../components/WizardPath/WizardPath";
import {
  AttributeSchema,
  ImageAttributeValue,
  DocumentAttributeValue,
  IproductReferenceCollectionItem,
  PIMProduct,
  ProductCreationRequestStateSchema,
  ProductFormSchema,
  TemplateProductFormStepItemSchema,
  TemplateProductFormStepSchema,
} from "../../../../../types/types.PIM";
import { mergeFormState } from "./mergeFormState";
import { handleCollectionFormValue } from "./utils";
import {
  processAttributeFormValue,
  ProductGroupFormValues,
} from "../../../ProductDetailPage/ProductOverview/product_overview.utils";
import {
  AddressValue,
  prepareAddressForForm,
} from "../../../../admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/address.utils";
import { Form } from "../../../../../layout/FormLayout";
import { useEffect, useRef, useState } from "react";
import { GroupAttributeField } from "./GroupAttributeField";
import { endpoints } from "../../../../../endpoints";
import {
  useDependentAttributes,
  UseDependentAttributesMultiResult,
} from "../../../../../util/hooks";
import type { MultiSelectRefType } from "../../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/type";
import useSWR from "swr";
import { ErrorPlaceholder } from "../../../../../components/Error";
import { DelayedSpinner } from "../../../../../components/DelayedSpinner/DelayedSpinner";
import { CollectionItems } from "./CollectionItems";
import axios from "axios";
import type { AxiosError } from "axios";
import { useNotifications } from "../../../../../components/Notifications/NotificationsContext";
import { DocumentsStep } from "./DocumentsStep";
import { OptionType, User } from "../../../../../types/types";
import { useHistory, useParams } from "react-router-dom";
import { ReviewStep } from "./ReviewStep";
import ReactTooltip from "react-tooltip";
import { useAuthContext } from "../../../../../components/Auth";
import { IDMobile } from "../../../../../components/IDs/IDs";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { getValidationForProductCollection } from "../../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/ProductCollectionSchema.util";
import {
  zodOptionalString,
  zodRequiredString,
} from "../../../../../util/zod.util";
import { ChipType } from "../../../../../components/Chips/Chips";
import { spacingTokens } from "../../../../../designTokens";

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-start;
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.primaryBG};
  z-index: 10;
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 0;
    right: 0;
    background: linear-gradient(
      180deg,
      rgb(3 3 3 / 6%) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    height: 10px;
  }
`;

const SaveButtonContainer = styled.div`
  display: flex;
  align-content: center;
  align-items: center;
  margin-left: auto;
`;

const StyledForm = styled(Form)`
  > div[class*="FormFieldAndIcon"] {
    margin-top: ${({ theme }) => theme.resolve("spacing_2xl")};
  }
`;

const GroupContainer = styled.div`
  div[class*="auto-filled-indicator"] {
    > svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const getItemValidation = ({
  input_type,
  is_multiple_choice,
  optional,
  t,
}: {
  input_type: AttributeSchema["input_type"];
  is_multiple_choice: boolean;
  optional: boolean;
  t: TFunction;
}) => {
  switch (input_type) {
    case "image":
      return optional
        ? z
            .object({
              asset_id: zodOptionalString.nullable(),
              caption: zodOptionalString.nullable(),
              image_dimensions: zodOptionalString.nullable(),
            })
            .optional()
            .nullable()
        : z.object({
            asset_id: zodRequiredString(t),
            caption: zodOptionalString.nullable(),
            image_dimensions: zodRequiredString(t),
          });

    default:
      return getValidationForProductCollection({
        input_type,
        is_multiple_choice,
        optional,
        t,
      });
  }
};

type CollectionRowValue = {
  attribute_id: string;
  value?:
    | string
    | boolean
    | null
    | { url: string; display_text: string }
    | IproductReferenceCollectionItem
    | ImageAttributeValue
    | DocumentAttributeValue;
};

export type CollectionRow = { values: CollectionRowValue[] };

export type CollectionValues =
  | Array<{ method: "delete"; collection_id: string; row_ids: string[] }>
  | Array<{ method: "post"; collection_id: string; rows: CollectionRow[] }>
  | Array<{
      method: "patch";
      collection_id: string;
      row_id: string;
      rows: CollectionRow[];
    }>;

export const NewProductForm = () => {
  const [formData, setFormData] = useState<ProductFormSchema | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(
    formData?.status === "submitted"
  );
  const [requiredDocumentsCategories, setRequiredDocumentsCategories] =
    useState<string[]>([]);

  const [submittingCollectionValues, setSubmittingCollectionValues] =
    useState(false);
  const [loadingCollectionId, setLoadingCollectionId] =
    useState<string | null>(null);
  const [
    isMissingRequiredCollectionValues,
    setIsMissingRequiredCollectionValues,
  ] = useState(false);
  const [
    missingRequiredCollectionValuesLabels,
    setMissingRequiredCollectionValuesLabels,
  ] = useState<string[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const { storefront_id, storefront_metadata, tenant_id } = useStoreState();
  const [product, setProduct] = useState<PIMProduct>();
  const [manualNavigation, setManualNavigation] = useState(false);
  const [currentStep, setCurrentStep] = useState(
    formData?.filled_steps?.length && !manualNavigation
      ? formData?.filled_steps[formData?.filled_steps?.length - 1]?.priority + 1
      : 0
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const constraintsFetchedForStep = useRef<number | null>(null);
  const { t } = useTranslation();
  const { form_id } = useParams<{ form_id: string }>();
  const { adminPath } = useRoutePath();
  const { user } = useAuthContext();
  const history = useHistory();
  const theme = useTheme();

  const { notifyError, notifySuccess } = useNotifications();

  const last_filled_step = useMemo(
    () => formData?.filled_steps[formData?.filled_steps?.length - 1],
    [formData]
  );

  const stepItems = useMemo(() => {
    return formData?.form?.steps?.[currentStep]?.items ?? [];
  }, [formData?.form?.steps, currentStep]);

  const defaultValues = useMemo(() => {
    return stepItems.reduce((acc, item) => {
      if (item.type === "group_attribute") {
        const attr = item.attributes[0];
        const input_type = attr.input_type;
        switch (input_type) {
          case "single_select":
            return {
              ...acc,
              [attr.attribute_id]: attr?.values?.length
                ? {
                    label: attr.values[0].enum_variant?.variant,
                    value: attr.values[0].enum_variant?.variant,
                  }
                : { label: "", value: "" },
            };
          case "multi_select":
            return {
              ...acc,
              [attr.attribute_id]: attr?.values?.length
                ? attr.values.map((value) => value.enum_variant?.variant)
                : [],
            };
          case "user":
            return {
              ...acc,
              [attr.attribute_id]: attr?.values?.length
                ? attr.values.map((value) =>
                    convertUserToOption(value.user as User, false)
                  )
                : attr.is_multiple_choice
                ? []
                : { label: "", value: "" },
            };
          case "product_reference":
            return {
              ...acc,
              [attr.attribute_id]: attr?.values?.length
                ? attr.values.map((value) => ({
                    label: value.product_ref?.name,
                    value: value.product_ref?.id,
                  }))
                : attr.is_multiple_choice
                ? []
                : { label: "", value: "" },
            };
          case "object_reference":
            return {
              ...acc,
              [attr.attribute_id]: attr?.values?.length
                ? attr.values.map((value) => ({
                    label: value.object_ref?.name,
                    value: value.object_ref?.id,
                  }))
                : attr.is_multiple_choice
                ? []
                : { label: "", value: "" },
            };
          case "address":
            return {
              ...acc,
              [attr.attribute_id]: attr.values?.length
                ? prepareAddressForForm(attr.values[0].value as AddressValue)
                : {},
            };
          case "document":
            return {
              ...acc,
              [attr.attribute_id]: attr.values?.length
                ? attr.values
                    .filter(
                      (value) =>
                        (value.value as DocumentAttributeValue)?.asset_id
                    )
                    .map((value) => ({
                      asset_id: (value.value as DocumentAttributeValue)
                        .asset_id,
                      value_id: value.id,
                    }))
                : [],
            };
          default:
            return {
              ...acc,
              [attr.attribute_id]: attr.values?.length
                ? attr.values[0].value
                : null,
            };
        }
      }
      return acc; // default values for collection_attribute is handled in the AddItemToCollectionForm component
    }, {});
  }, [stepItems]);

  const validator = useMemo(() => {
    return z.object(
      stepItems.reduce((acc, item) => {
        if (item.type === "group_attribute") {
          const attr = item.attributes[0];
          return {
            ...acc,
            [attr.attribute_id]: getItemValidation({
              input_type: attr.input_type as AttributeSchema["input_type"],
              is_multiple_choice: attr.is_multiple_choice ?? false,
              optional: !attr.is_required,
              t,
            }),
          };
        } else {
          return acc; // collection_attribute validation is handled in the AddItemToCollectionForm component
        }
      }, {})
    );
  }, [stepItems, t]);

  const methodsOfUseForm = useFormWrapper<ProductGroupFormValues>({
    defaultValues,
    resolver: zodResolver(validator),
  });

  const { handleSubmit: handleSubmitProductForm } = methodsOfUseForm;

  const multiSelectRefsForDependent = useRef<
    Record<string, MultiSelectRefType>
  >({});
  const allStepAttributes = useMemo(
    () =>
      formData?.form?.steps
        ?.flatMap((s) => s.items ?? [])
        ?.flatMap((item) =>
          item.attributes.map((a) => ({
            id: a.attribute_id || a.id,
            input_type: a.input_type,
          }))
        ) ?? [],
    [formData?.form?.steps]
  );
  const {
    clearableAttributes,
    dependentAttributesByContext,
    updateDependentAttributes: updateDependentAttributesFromHook,
  } = useDependentAttributes({
    mode: "multi",
    storefrontId: storefront_id,
    enabled: !!storefront_metadata?.enable_reference_entities,
    getFormValues: () => methodsOfUseForm.getValues(),
    setFormValue: (attrId, value, config) =>
      methodsOfUseForm.setValue(
        attrId,
        value as Parameters<typeof methodsOfUseForm.setValue>[number],
        config
      ),
    attributesOrColumns: allStepAttributes,
    multiSelectRefs: multiSelectRefsForDependent,
    getRelevantAttributeIds: (contextId) => {
      return new Set(
        stepItems
          .filter((item) => item.group_or_collection_id === contextId)
          .flatMap((item) =>
            item.attributes.map((attr) => attr.attribute_id || attr.id)
          )
      );
    },
  });

  const {
    data: productCreationFormData,
    mutate: mutateProductCreationFormData,
    error: productCreationFormDataError,
  } = useSWR<ProductFormSchema>(
    endpoints.v2_storefronts_id_pim_product_creation_form_id(
      storefront_id,
      form_id
    ),
    {
      revalidateOnFocus: false,
    }
  );

  /**
   * Refresh the saved values after a step save without re-fetching the whole
   * form. Fetches the lightweight `/state` endpoint and merges it into the SWR
   * cache (no revalidation), so consumers see the updated values via the same
   * `productCreationFormData` flow as a full re-fetch.
   */
  const refreshFormState = useCallback(async () => {
    const requestId = productCreationFormData?.id;
    if (!requestId) return;
    const { data: state } = await axios.get<ProductCreationRequestStateSchema>(
      endpoints.v2_storefronts_id_pim_product_creation_form_id_state(
        storefront_id,
        requestId
      )
    );
    await mutateProductCreationFormData(
      (current) => (current ? mergeFormState(current, state) : current),
      false
    );
  }, [
    productCreationFormData?.id,
    storefront_id,
    mutateProductCreationFormData,
  ]);

  const getGroupValues = (values: ProductGroupFormValues) => {
    let groupValues: {
      value: string | boolean | Record<string, string> | null;
      attribute_id: string;
      group_id: string;
    }[] = [];
    Object.keys(values).forEach((key) => {
      const item =
        stepItems.find((item) =>
          item.attributes.find((attribute) => attribute.attribute_id === key)
        ) ?? null;
      const attribute =
        item?.attributes?.find((attribute) => attribute.attribute_id === key) ??
        null;
      if (attribute?.input_type === "document") {
        const documentValues = handleCollectionFormValue(values[key]);
        if (Array.isArray(documentValues) && documentValues.length > 0) {
          groupValues.push(
            ...documentValues.map((value) => ({
              value: value as { asset_id: string },
              attribute_id: key,
              group_id: item?.group_or_collection_id ?? "",
            }))
          );
        } else {
          groupValues.push({
            value: "",
            attribute_id: key,
            group_id: item?.group_or_collection_id ?? "",
          });
        }
      } else if (
        attribute?.input_type === "multi_select" ||
        attribute?.is_multiple_choice
      ) {
        const multiSelectValues = Array.isArray(values[key]) ? values[key] : [];
        if (
          (multiSelectValues as string[] | ChipType[] | OptionType[]).length > 0
        ) {
          groupValues.push(
            ...(multiSelectValues as string[] | ChipType[] | OptionType[]).map(
              (value) => ({
                value:
                  typeof value === "string"
                    ? value
                    : Object.hasOwn(value, "name")
                    ? ((value as ChipType)?.id as string | undefined) ||
                      (value as ChipType)?.name
                    : (value as OptionType<string>).value,
                attribute_id: key,
                group_id: item?.group_or_collection_id ?? "",
              })
            )
          );
        } else {
          groupValues.push({
            value: "",
            attribute_id: key,
            group_id: item?.group_or_collection_id ?? "",
          });
        }
      } else {
        if (item) {
          if (
            attribute?.input_type === "toggle" ||
            attribute?.input_type === "checkbox"
          ) {
            // for some weird reasons, sometimes react-form library turns boolean to ['on'] for true and [] for false
            groupValues.push({
              value:
                Array.isArray(values[key]) &&
                (values[key] as Array<any>).length > 0
                  ? true
                  : (values[key] as boolean),
              attribute_id: key,
              group_id: item?.group_or_collection_id ?? "",
            });
          } else if (attribute?.input_type === "address") {
            groupValues.push({
              value: processAttributeFormValue(
                values[key] as Exclude<
                  ProductGroupFormValues[string],
                  OptionType<string>[] | string[]
                >,
                "address"
              ),
              attribute_id: key,
              group_id: item?.group_or_collection_id ?? "",
            });
          } else {
            const value = (() => {
              if (typeof values[key] === "object" && values[key] !== null) {
                if (
                  "value" in (values[key] as Record<string, unknown>) &&
                  "label" in (values[key] as Record<string, unknown>)
                ) {
                  return (values[key] as Record<string, unknown>)
                    .value as string;
                }
                const object_values = Object.values(
                  values[key] as Record<string, unknown>
                );
                if (
                  object_values.length > 0 &&
                  object_values.some(
                    (val) => val !== undefined && val !== null && val !== ""
                  )
                ) {
                  return values[key] as Record<string, string>;
                }
                return null;
              }
              return (values[key] as string | boolean) ?? null;
            })();
            groupValues.push({
              value,
              attribute_id: key,
              group_id: item.group_or_collection_id,
            });
          }
        }
      }
    });
    return groupValues;
  };

  const onProductFormSubmit = async (values: ProductGroupFormValues) => {
    const payload = {
      step_id: productCreationFormData?.form?.steps?.[currentStep]?.id,
      group_values: getGroupValues(values),
      is_step_completed: true,
    };
    setIsLoading(true);
    try {
      if (currentStep === formData?.form?.steps?.length) {
        await axios.post(
          `/v2/storefronts/${storefront_id}/pim/forms/${productCreationFormData?.form?.id}/product-creation-form/${productCreationFormData?.id}`
        );
        notifySuccess(t("Product Form Submitted Successfully"));
        mutateProductCreationFormData();
        setFormSubmitted(true);
      } else {
        await axios.patch(
          `/v2/storefronts/${storefront_id}/pim/forms/${productCreationFormData?.form?.id}/product-creation-form/${productCreationFormData?.id}`,
          payload
        );
        await refreshFormState();
        notifySuccess(t("Data saved successfully"));
        setCurrentStep(currentStep + 1);
        setManualNavigation(false);
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      notifyError(
        axiosError.response?.data.message ??
          t("There was an error saving the data"),
        { error: axiosError }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onCreateImageAttr = useCallback(
    (
      image_attr: ImageAttributeValue | null,
      attribute: AttributeSchema & { attribute_id?: string }
    ) => {
      methodsOfUseForm.setValue(
        attribute.attribute_id ? attribute.attribute_id : attribute.id,
        image_attr
      );
    },
    [methodsOfUseForm]
  );

  const onCreateDocumentAttr = useCallback(
    (
      docs: DocumentAttributeValue[],
      attribute: AttributeSchema & { attribute_id?: string }
    ) => {
      methodsOfUseForm.setValue(
        attribute.attribute_id ? attribute.attribute_id : attribute.id,
        docs.map(({ asset_id }) => ({ asset_id }))
      );
    },
    [methodsOfUseForm]
  );

  // Reference Entity constraint calculation helpers
  const getHasReferenceEntityMapping = useCallback(
    (groupOrCollectionId: string, type?: string): boolean => {
      if (!storefront_metadata?.enable_reference_entities) return false;
      if (type === "group_attribute") {
        return (
          product?.product_schema.groups.find(
            (g) => g.id === groupOrCollectionId
          )?.has_reference_entity_mapping ?? false
        );
      }
      return (
        product?.product_schema.collections.find(
          (c) => c.id === groupOrCollectionId
        )?.has_reference_entity_mapping ?? false
      );
    },
    [product, storefront_metadata?.enable_reference_entities]
  );

  const updateDependentAttributes = useCallback(
    (
      params: Parameters<
        UseDependentAttributesMultiResult["updateDependentAttributes"]
      >[0]
    ) => {
      const { contextId, contextType } = params;
      if (
        !getHasReferenceEntityMapping(
          contextId,
          contextType === "group" ? "group_attribute" : "collection_attribute"
        )
      ) {
        return;
      }
      updateDependentAttributesFromHook(params);
    },
    [getHasReferenceEntityMapping, updateDependentAttributesFromHook]
  );

  const handleDiscard = async () => {
    setIsDiscarding(true);
    try {
      await axios.patch(
        `/v2/storefronts/${storefront_id}/pim/product-creation-form/${productCreationFormData?.id}/status`,
        {
          status: "discard",
        }
      );
      notifySuccess(t("Product form discarded successfully"));
      mutateProductCreationFormData();
      history.replace(`${adminPath}/pim/products`);
    } catch (error) {
      const axiosError = error as AxiosError;
      notifyError(
        axiosError.response?.data.message ??
          t("There was an error discarding the product form"),
        { error: axiosError }
      );
    } finally {
      setIsDiscarding(false);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    const payload = {
      step_id: productCreationFormData?.form?.steps?.[currentStep]?.id,
      group_values: getGroupValues(methodsOfUseForm.getValues()).filter(
        (group) => group.value !== undefined && group.value !== null
      ),
      is_step_completed: false,
    };
    await axios.patch(
      `/v2/storefronts/${storefront_id}/pim/forms/${productCreationFormData?.form?.id}/product-creation-form/${productCreationFormData?.id}`,
      currentStep === formData?.form?.steps?.length ? undefined : payload
    );
    await refreshFormState();
    notifySuccess(t("Data saved successfully"));
    setIsLoading(false);
  };

  const handleCreateProduct = async () => {
    setIsLoading(true);
    try {
      const response = await axios.patch<ProductFormSchema>(
        `/v2/storefronts/${storefront_id}/pim/product-creation-form/${productCreationFormData?.id}/status`,
        {
          status: "create",
        }
      );
      notifySuccess(t("Product created successfully"));
      history.replace(
        `${adminPath}/pim/products/${response.data?.new_product_id}`
      );
    } catch (error) {
      const axiosError = error as AxiosError;
      notifyError(
        axiosError.response?.data.message ??
          t("There was an error creating the product"),
        { error: axiosError }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleWizardPathClick = (index: number) => {
    if (index <= (last_filled_step?.priority ?? 0)) {
      setManualNavigation(true);
      setCurrentStep(index);
    }
  };

  const handleCollectionValues = async (collectionValues: CollectionValues) => {
    if (submittingCollectionValues) return;
    setSubmittingCollectionValues(true);
    setLoadingCollectionId(collectionValues[0].collection_id ?? "");
    setManualNavigation(true);
    const method = collectionValues[0].method;
    const payload = {
      step_id: productCreationFormData?.form?.steps?.[currentStep]?.id,
      ...(method === "delete"
        ? {
            collection_id: collectionValues[0].collection_id,
            row_ids: collectionValues.flatMap(
              (val) => (val as { row_ids: string[] }).row_ids
            ),
          }
        : {
            collection_values: collectionValues.map(
              ({ method, ...payload }) => payload
            ),
          }),
      is_step_completed: false,
    };
    try {
      await axios[collectionValues[0].method](
        `/v2/storefronts/${storefront_id}/pim/forms/${productCreationFormData?.form?.id}/product-creation-form/${productCreationFormData?.id}/rows`,
        method !== "delete"
          ? payload
          : {
              data: payload,
            }
      );
      await refreshFormState();
    } catch (error) {
      const axiosError = error as AxiosError;
      notifyError(
        axiosError.response?.data.message ??
          t("There was an error saving the data"),
        { error: axiosError }
      );
    } finally {
      setSubmittingCollectionValues(false);
    }
  };

  // Fetch constraints on initial load when step contains groups/collections with reference entity mapping
  useEffect(() => {
    if (!stepItems || !product) return;

    // Prevent infinite loop - only fetch once per step
    if (constraintsFetchedForStep.current === currentStep) return;
    constraintsFetchedForStep.current = currentStep;

    const contextMap: Record<
      string,
      { type: "group" | "collection"; existingValues: Record<string, unknown> }
    > = {};

    stepItems.forEach((item: TemplateProductFormStepItemSchema) => {
      const contextType =
        item.type === "group_attribute" ? "group" : "collection";
      if (
        getHasReferenceEntityMapping(item.group_or_collection_id, item.type)
      ) {
        const prevExistingValues =
          contextMap[item.group_or_collection_id]?.existingValues ?? {};
        contextMap[item.group_or_collection_id] = {
          type: contextType,
          existingValues: {
            ...prevExistingValues,
            ...item.attributes.reduce((acc, attr) => {
              const attr_values = attr.values ?? [];
              if (attr_values.length) {
                if (attr.is_multiple_choice || contextType === "collection") {
                  acc[attr.attribute_id || attr.id] = attr_values.map((v) => ({
                    name:
                      v.enum_variant?.variant || attr.input_type === "user"
                        ? convertUserToOption(v as unknown as User).value
                        : attr.input_type === "product_reference"
                        ? v.product_ref?.id
                        : attr.input_type === "object_reference"
                        ? v.object_ref?.id
                        : v.value,
                  })); // for multi-select, send array of values
                } else {
                  acc[attr.attribute_id || attr.id] =
                    typeof attr_values[0].value === "string"
                      ? attr_values[0].value
                      : attr.input_type === "user"
                      ? convertUserToOption(attr_values[0] as unknown as User)
                      : attr.input_type === "product_reference"
                      ? attr_values[0].product_ref?.id
                      : attr.input_type === "object_reference"
                      ? attr_values[0].object_ref?.id
                      : attr_values[0].value; // for single select, send single value
                }
              }
              return acc;
            }, {} as Record<string, unknown>),
          },
        };
      }
    });
    Object.entries(contextMap).forEach(
      ([contextId, { type, existingValues }]) => {
        updateDependentAttributes({
          contextId,
          contextType: type,
          clonedSelectionsValues: Object.keys(existingValues).length
            ? existingValues
            : undefined,
        });
      }
    );
  }, [
    currentStep,
    getHasReferenceEntityMapping,
    product,
    stepItems,
    updateDependentAttributes,
  ]);

  useEffect(() => {
    const productId = productCreationFormData?.product_id;
    if (!product && productId && tenant_id) {
      const source = axios.CancelToken.source();
      const fetchProductData = async () => {
        try {
          const { data } = await axios.get<PIMProduct>(
            endpoints.v2_tenants_id_pim_products_id(tenant_id, productId),
            { params: { use_database: true }, cancelToken: source.token }
          );
          setProduct(data);
        } catch (error) {
          if (!axios.isCancel(error)) {
            const axiosError = error as AxiosError;
            notifyError(
              axiosError.response?.data.message ??
                t("There was an error loading the product"),
              { error: axiosError }
            );
          }
        }
      };
      fetchProductData();
      return () => source.cancel();
    }
  }, [product, productCreationFormData?.product_id, tenant_id, notifyError, t]);

  useEffect(() => {
    if (productCreationFormData) {
      setLabels([
        ...(productCreationFormData?.form?.steps?.map(
          (step: TemplateProductFormStepSchema) => step.name ?? ""
        ) ?? []),
        !formSubmitted ? t("Review & Submit") : t("Review"),
      ]);
      setFormData(productCreationFormData);
      setFormSubmitted(productCreationFormData?.status !== "pending");
      setCurrentStep(
        formData?.form?.steps &&
          (productCreationFormData?.status !== "pending" ||
            user?.email_address !== formData?.user?.email_address)
          ? formData?.form?.steps.length
          : manualNavigation
          ? currentStep
          : formData?.filled_steps?.length
          ? formData?.filled_steps[formData?.filled_steps?.length - 1]?.priority
          : 0
      );
    }
  }, [
    productCreationFormData,
    formData,
    t,
    user,
    formSubmitted,
    manualNavigation,
    currentStep,
  ]);

  useEffect(() => {
    let collectionNames: string[] = [];
    if (
      formData?.form?.steps?.[currentStep]?.items?.some(
        (item) =>
          item.type === "collection_attribute" &&
          item.attributes?.some((attribute) => attribute.is_required) &&
          (item.value_rows?.length ?? 0) === 0
      )
    ) {
      formData?.form?.steps?.[currentStep]?.items?.forEach((item) => {
        if (
          item.type === "collection_attribute" &&
          item.attributes?.some((attribute) => attribute.is_required) &&
          (item.value_rows?.length ?? 0) === 0
        ) {
          collectionNames.push(item.group_or_collection ?? "");
        }
      });
      setMissingRequiredCollectionValuesLabels(collectionNames);
      setIsMissingRequiredCollectionValues(true);
    } else {
      setIsMissingRequiredCollectionValues(false);
      setMissingRequiredCollectionValuesLabels([]);
    }
  }, [formData?.form?.steps, currentStep]);

  if (productCreationFormDataError) {
    return (
      <ErrorPlaceholder
        message={
          productCreationFormDataError?.response?.data?.message ??
          t("There was an error loading the product creation form")
        }
        showBackButton={true}
        backUrl={`${adminPath}/pim/products`}
      />
    );
  }
  if (!productCreationFormData) {
    return <DelayedSpinner />;
  }
  return (
    <PageWrapper>
      <PageHeader>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: spacingTokens.spacing_md,
              }}
            >
              <XLTitle
                style={{ margin: 0, fontSize: theme.resolve("font_size_3xl") }}
              >
                {formData?.product_name}
              </XLTitle>
              <IDMobile style={{ display: "inline", marginLeft: "5px" }}>
                {formData?.number}
              </IDMobile>
            </div>
            <SubheaderInformationContainer
              style={{ justifyContent: "flex-start" }}
            >
              <SubheaderContainer>
                <TemplatesNavIcon width={16} height={16} />
                <InnerTitleText>{t("Form")}:</InnerTitleText>
                <SubheaderContentText>
                  {formData?.form?.name}
                </SubheaderContentText>
              </SubheaderContainer>

              {formData?.created_by_user && (
                <SubheaderContainer>
                  <UserIcon width={16} height={16} />
                  <InnerTitleText>{t("Requested By")}:</InnerTitleText>
                  <SubheaderContentText>
                    {formData?.created_by_user?.firstname}{" "}
                    {formData?.created_by_user?.lastname}
                  </SubheaderContentText>
                </SubheaderContainer>
              )}
              {formData?.created_at && (
                <SubheaderContainer>
                  <CalendarIcon width={16} height={16} />
                  <InnerTitleText>{t("Requested On")}:</InnerTitleText>
                  <SubheaderContentText>
                    {formatDateTime(formData?.created_at)}
                  </SubheaderContentText>
                </SubheaderContainer>
              )}
            </SubheaderInformationContainer>
          </div>

          <SaveButtonContainer>
            <StatusLeft
              color={
                productCreationFormData?.status === "discarded"
                  ? "red"
                  : formSubmitted
                  ? "green"
                  : productCreationFormData?.status === "completed"
                  ? "blue"
                  : "yellow"
              }
              textStyle={{
                fontSize: theme.resolve("font_size_sm"),
                marginLeft: "-3px",
              }}
              text={
                productCreationFormData?.status === "discarded"
                  ? t("Discarded")
                  : productCreationFormData?.status === "completed"
                  ? t("Completed")
                  : formSubmitted
                  ? t("Submitted")
                  : t("In Progress")
              }
            />
            <TertiaryButtonMediumV2
              onClick={() => {
                history.replace(`${adminPath}/pim/products`);
              }}
              style={{
                margin: `0 ${theme.resolve("spacing_md")} 0 ${theme.resolve(
                  "spacing_2xl"
                )}`,
              }}
              disabled={isLoading}
            >
              {t("Close")}
            </TertiaryButtonMediumV2>
            {productCreationFormData?.status !== "discarded" &&
              productCreationFormData?.status !== "completed" &&
              user?.email_address ===
                formData?.created_by_user?.email_address && (
                <ButtonWithConfirmDialog
                  Button={DestructiveButtonMediumV2}
                  testid="discard-button"
                  loading={isDiscarding}
                  confirmMessage={t(
                    "This form will be marked as discarded and cannot be used to create a product. This action cannot be undone. Are you sure you want to continue?"
                  )}
                  handleConfirm={handleDiscard}
                  buttonText={t("Discard")}
                  confirmBtnLabel={t("Discard")}
                  confirmBtnType="destructive"
                  useV2Buttons={true}
                />
              )}
            {productCreationFormData?.status === "pending" &&
              user?.email_address === formData?.user?.email_address &&
              currentStep !== formData?.form?.steps?.length && (
                <PrimaryButtonMediumV2
                  onClick={handleSave}
                  disabled={
                    isLoading || isDiscarding || submittingCollectionValues
                  }
                  loading={isLoading}
                  style={{ margin: `0 0 0 ${theme.resolve("spacing_md")}` }}
                >
                  {t("Save")}
                </PrimaryButtonMediumV2>
              )}

            {formSubmitted &&
              productCreationFormData?.status !== "discarded" &&
              productCreationFormData?.status !== "completed" &&
              (user?.email_address ===
                formData?.created_by_user?.email_address ||
                user?.rbac_role === "agilis_admin") && (
                <PrimaryButtonMediumV2
                  onClick={handleCreateProduct}
                  disabled={
                    isLoading || isDiscarding || submittingCollectionValues
                  }
                  loading={isLoading}
                  style={{ margin: `0 0 0 ${theme.resolve("spacing_md")}` }}
                >
                  {t("Create Product")}
                </PrimaryButtonMediumV2>
              )}
          </SaveButtonContainer>
        </div>
      </PageHeader>
      <ContentWrapper style={{ padding: `0 ${theme.resolve("spacing_6xl")}` }}>
        <div
          style={{
            marginBottom:
              currentStep === formData?.form?.steps?.length
                ? "0px"
                : theme.resolve("spacing_xl"),
          }}
        >
          <WizardPath
            labels={labels}
            currentPoint={currentStep}
            completed={formSubmitted}
            onClick={handleWizardPathClick}
          />
        </div>
        <WhiteInnerCard
          style={{
            borderWidth:
              currentStep === formData?.form?.steps?.length ? "0px" : "1px",
            borderColor:
              currentStep === formData?.form?.steps?.length
                ? "transparent"
                : theme.primaryBorder,
            padding:
              currentStep === formData?.form?.steps?.length ? "0" : "20px",
          }}
        >
          {currentStep === formData?.form?.steps?.length ? (
            <></>
          ) : (
            <>
              <H4>{formData?.form?.steps?.[currentStep]?.name ?? ""}</H4>
              <FullWidthHorizontalSeparator
                margin={`${theme.resolve("spacing_md")} 70px ${theme.resolve(
                  "spacing_md"
                )} 70px`}
              />
            </>
          )}

          <StyledForm
            noValidate
            style={{ paddingTop: theme.resolve("spacing_4xl") }}
          >
            {formData &&
              formData.form?.steps?.[currentStep]?.is_document_step && (
                <DocumentsStep
                  formData={formData}
                  productId={productCreationFormData?.product_id}
                  setRequiredDocsCategories={setRequiredDocumentsCategories}
                />
              )}
            {currentStep === formData?.form?.steps?.length && (
              <ReviewStep
                formData={formData}
                setCurrentStep={setCurrentStep}
                productId={productCreationFormData?.product_id}
                setManualNavigation={setManualNavigation}
                formSubmitted={formSubmitted}
                formStatus={productCreationFormData?.status}
              />
            )}
            {formData?.form?.steps?.[currentStep]?.items?.map(
              (item: TemplateProductFormStepItemSchema) => {
                if (item.type === "group_attribute") {
                  const hasMapping = getHasReferenceEntityMapping(
                    item.group_or_collection_id,
                    item.type
                  );
                  return (
                    <GroupContainer
                      key={`${item.group_or_collection_id}-${item.attributes[0].attribute_id}-${currentStep}`}
                    >
                      <GroupAttributeField
                        attributeId={item.attributes[0].attribute_id}
                        attr={
                          {
                            ...product?.product_schema.groups
                              ?.find(
                                (group) =>
                                  group.id === item.group_or_collection_id
                              )
                              ?.attributes?.find(
                                (attribute) =>
                                  attribute.id ===
                                  item.attributes[0].attribute_id
                              ),
                            ...item.attributes[0],
                            id: item.attributes[0].attribute_id,
                          } as AttributeSchema
                        }
                        methodsOfUseForm={methodsOfUseForm}
                        onCreateImageAttribute={onCreateImageAttr}
                        onCreateDocumentAttribute={onCreateDocumentAttr}
                        product={product}
                        hasReferenceEntityMapping={hasMapping}
                        dependentAttributes={
                          dependentAttributesByContext[
                            item.group_or_collection_id
                          ]
                        }
                        onFieldChange={(attributeId, clearSelection) => {
                          if (
                            hasMapping &&
                            !!attributeId &&
                            dependentAttributesByContext[
                              item.group_or_collection_id
                            ] &&
                            dependentAttributesByContext[
                              item.group_or_collection_id
                            ].find(
                              (constr) => constr.attribute_id === attributeId
                            ) !== undefined
                          ) {
                            updateDependentAttributes({
                              contextId: item.group_or_collection_id,
                              contextType: "group",
                              attributeId,
                              clearSelection,
                            });
                          }
                        }}
                        clearableAttributes={clearableAttributes}
                        multiSelectRefs={multiSelectRefsForDependent}
                      />
                    </GroupContainer>
                  );
                }

                const hasCollectionMapping = getHasReferenceEntityMapping(
                  item.group_or_collection_id,
                  item.type
                );
                return (
                  <CollectionItems
                    key={`${item.group_or_collection_id}-${item.attributes[0].attribute_id}-${currentStep}`}
                    stepItem={item}
                    onSuccess={() => {}}
                    isLoading={
                      submittingCollectionValues &&
                      item.group_or_collection_id === loadingCollectionId
                    }
                    rows={item.value_rows || []}
                    columns={
                      Array.isArray(item.columns) &&
                      Array.isArray(item.attributes)
                        ? item.columns.reduce((columns, column) => {
                            const attribute = item.attributes.find(
                              (attr) => attr.name === column.name
                            );
                            if (attribute) {
                              columns.push({
                                ...column,
                                is_multiple_choice:
                                  attribute.is_multiple_choice ??
                                  column.is_multiple_choice ??
                                  false,
                              });
                            }
                            return columns;
                          }, [] as typeof item.columns)
                        : []
                    }
                    handleCollectionValues={handleCollectionValues}
                    hasReferenceEntityMapping={hasCollectionMapping}
                    collectionId={item.group_or_collection_id}
                  />
                );
              }
            )}
            <ButtonContainer>
              {currentStep > 0 &&
                productCreationFormData?.status === "pending" &&
                user?.email_address === formData?.user?.email_address && (
                  <TertiaryButtonMediumV2
                    onClick={() => {
                      setManualNavigation(true);
                      setCurrentStep(currentStep - 1);
                    }}
                    disabled={isLoading}
                    type="button"
                  >
                    {t("Back")}
                  </TertiaryButtonMediumV2>
                )}
              {productCreationFormData?.status === "pending" &&
                user?.email_address === formData?.user?.email_address && (
                  <PrimaryButtonMediumV2
                    loading={isLoading}
                    onClick={handleSubmitProductForm(onProductFormSubmit)}
                    data-tip={
                      formData?.form?.steps?.[currentStep]?.is_document_step &&
                      requiredDocumentsCategories.length > 0
                        ? t("Please add the required documents")
                        : isMissingRequiredCollectionValues
                        ? t(
                            "Please add the required collection values for {{labels}}",
                            {
                              labels: missingRequiredCollectionValuesLabels
                                .map((label) => `"${label}"`)
                                .join(", "),
                            }
                          )
                        : ""
                    }
                    data-for={"required-documents-tooltip"}
                    disabled={
                      isLoading ||
                      submittingCollectionValues ||
                      isMissingRequiredCollectionValues ||
                      isDiscarding ||
                      productCreationFormData?.status !== "pending" ||
                      (formData?.form?.steps?.[currentStep]?.is_document_step &&
                        requiredDocumentsCategories.length > 0)
                    }
                  >
                    {currentStep === formData?.form?.steps?.length
                      ? t("Submit")
                      : t("Next")}
                  </PrimaryButtonMediumV2>
                )}
            </ButtonContainer>
            <ReactTooltip id="required-documents-tooltip" />
          </StyledForm>
        </WhiteInnerCard>
      </ContentWrapper>
    </PageWrapper>
  );
};
